import {
  Box,
  Card,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ApprovalRequestStatus } from 'app/enums/ApprovalRequestStatus';
import { CourseType } from 'app/enums/CourseType';
import { IProject } from 'app/models/responses/IProject';
import { IProjectExtraTimeRequest } from 'app/models/responses/IProjectExtraTimeRequest';
import { ExceptionHandler } from 'components';
import { rootConfig } from 'config';
import useMounted from 'hooks/useMounted';
import { useSnackbar } from 'notistack';
import { useState, useCallback, useEffect } from 'react';
import axios from 'utils/axios';
import buildQuery from 'odata-query';
import variableCaseToSentence from 'utils/variableCaseToSentence';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import { IProjectClient } from 'app/models/entities/IProjectClient';

interface IProps {
  project: IProject;
  projectClient: IProjectClient;
}

const Details = ({ project, projectClient }: IProps) => {
  const mounted = useMounted();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setEditing] = useState(false);

  const [approvedExtraTimeTotal, setApprovedExtraTimeTotal] = useState<number>(0);

  const getExtraTimeRequests = useCallback(async () => {
    try {
      const select = ['durationInDays'];
      const filter = {
        projectId: project.id,
        status: ApprovalRequestStatus[ApprovalRequestStatus.Approved],
      };
      const queryString = buildQuery({ select, filter });
      const response = await axios.get(
        `${rootConfig.odataRoute}/projectExtraTimeRequests${queryString}`,
      );
      if (mounted.current) {
        setApprovedExtraTimeTotal(
          response.data.value.reduce(
            (total: number, currentValue: IProjectExtraTimeRequest) =>
              total + (currentValue?.durationInDays || 0),
            0,
          ),
        );
      }
    } catch (error) {
      enqueueSnackbar(<ExceptionHandler exception={error} />, { variant: 'error' });
    }
  }, [mounted, project.id, enqueueSnackbar]);

  useEffect(() => {
    getExtraTimeRequests();
  }, [getExtraTimeRequests]);

  return (
    <Card>
      <CardHeader title='Details' />
      <Divider />
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Course
              </Typography>
            </TableCell>
            <TableCell>
              <Grid container spacing={1} alignItems='center'>
                <Grid item>
                  <Typography color='textSecondary' variant='body2'>
                    {project.courseRevision!.course!.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Chip
                    label={project.courseRevision!.course!.type}
                    variant='outlined'
                    color='secondary'
                    size='small'
                  />
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Duration
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {project.courseRevision!.durationInDays}
                {approvedExtraTimeTotal ? ` + ${approvedExtraTimeTotal} ` : ''}
                {' days'}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Delivery Method
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {project.projectDeliveryMethod?.name!}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Type
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {variableCaseToSentence(project.type!)}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Learner Type
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {variableCaseToSentence(project.learnerType!)}
              </Typography>
            </TableCell>
          </TableRow>
          {project.projectClients && (
            <TableRow>
              <TableCell>
                <Typography color='textPrimary' variant='subtitle2'>
                  Client(s)
                </Typography>
              </TableCell>
              <TableCell>
                {project.projectClients.map((projectClient, index) => (
                  <Typography color='textSecondary' variant='body2'>
                    {projectClient.client!.name}
                    {index < project.projectClients!.length - 1 && ', '}
                  </Typography>
                ))}
              </TableCell>
            </TableRow>
          )}

          {project.group && (
            <TableRow>
              <TableCell>
                <Typography color='textPrimary' variant='subtitle2'>
                  Group
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color='textSecondary' variant='body2'>
                  {project.group}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                Venue
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {project.venue?.name}
              </Typography>
            </TableCell>
          </TableRow>
          {project.courseRevision!.course!.type === CourseType[CourseType.SkillsProgramme] && (
            <TableRow>
              <TableCell>
                <Typography color='textPrimary' variant='subtitle2'>
                  Is Accredited?
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color='textSecondary' variant='body2'>
                  {project.isAccredited ? (
                    <CheckIcon fontSize='small' />
                  ) : (
                    <ClearIcon fontSize='small' />
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <Typography color='textPrimary' variant='subtitle2'>
                EPOE
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color='textSecondary' variant='body2'>
                {project.isEpoe ? <CheckIcon fontSize='small' /> : <ClearIcon fontSize='small' />}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
};

export default Details;
